import type { Result } from 'true-myth'

import { CountSchema } from '@/lib/facts/values'
import type { Locale } from '@/i18n/routing'

import { attempt, formatIcu, SENTINEL_PATTERN, sentinelIndex, type MessageFailure } from './icu'

/*
 * A message a client island formats against a value only the client knows — a selected
 * passenger count, the signed-in name. The server hands the island the message's source in
 * one locale; the island renders it on each change through the ICU half of the grammar
 * the server renders by, and a template is never markdown: a client island parses none.
 */

/** The kinds a client template's values take: text the client holds, or a count it
 *  formats. */
export type TemplateKind = 'text' | 'count'

/** One locale's source of a store message, where it came from, so a failure names the
 *  file and key, and the kind of each argument its registry entry gives it. */
export interface MessageTemplate {
    readonly locale: Locale
    readonly source: string
    readonly file: string
    readonly key: string
    readonly kinds: Readonly<Record<string, TemplateKind>>
}

/** A client template's values, each read as the kind its registry entry gives it. */
export type TemplateValues = Readonly<Record<string, string | number>>

/** A value of the wrong kind is the caller's defect, not the copy's, so it throws. */
function typed(kind: TemplateKind, name: string, value: string | number) {
    if (kind === 'count') {
        return { kind, value: CountSchema.parse(value) } as const
    }
    if (typeof value !== 'string') {
        throw new TypeError(`{${name}} is text, not ${typeof value}`)
    }
    return { kind, value } as const
}

export function renderTemplate(
    { locale, source, kinds }: MessageTemplate,
    values: TemplateValues,
): Result<string, MessageFailure> {
    const variables = Object.fromEntries(
        Object.entries(values).flatMap(([name, value]) =>
            Object.hasOwn(kinds, name) ? [[name, typed(kinds[name], name, value)]] : [],
        ),
    )
    return attempt(() => {
        const { output, slots } = formatIcu(source, {
            locale,
            lookup: (name) =>
                Object.hasOwn(variables, name)
                    ? { status: 'present', variable: variables[name] }
                    : { status: 'absent' },
        })
        const number = new Intl.NumberFormat(locale)
        return output.replace(SENTINEL_PATTERN, (char) => {
            // Every sentinel left after the missing-variable check stands for a value.
            const variable = slots[sentinelIndex(char)].variable!
            switch (variable.kind) {
                case 'count':
                    return number.format(variable.value)
                case 'text':
                    return variable.value
                default:
                    throw new Error(`A template takes text and counts, not ${variable.kind}`)
            }
        })
    })
}
